import styled from 'styled-components';

export const StyledButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  isLoading?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  text-decoration: none;

  /* Size variants */
  ${({ size = 'md', theme }) => {
    switch (size) {
      case 'sm':
        return `
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          font-size: ${theme.fontSizes.sm};
          min-height: 2rem;
        `;
      case 'lg':
        return `
          padding: ${theme.spacing.md} ${theme.spacing.xl};
          font-size: ${theme.fontSizes.lg};
          min-height: 3rem;
        `;
      default:
        return `
          padding: ${theme.spacing.sm} ${theme.spacing.lg};
          font-size: ${theme.fontSizes.base};
          min-height: 2.5rem;
        `;
    }
  }}

  /* Color variants */
  ${({ variant = 'primary', theme }) => {
    switch (variant) {
      case 'secondary':
        return `
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.textInverse};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.secondaryHover};
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          border-color: ${theme.colors.primary};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary};
            color: ${theme.colors.textInverse};
          }
        `;
      case 'ghost':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.backgroundSecondary};
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.danger};
          color: ${theme.colors.textInverse};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.dangerHover};
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.textInverse};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryHover};
          }
        `;
    }
  }}

  /* Full width */
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}

  /* Loading state */
  ${({ isLoading }) => isLoading && 'pointer-events: none;'}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Responsive adjustments */
  @media ${({ theme }) => theme.media.touch} {
    /* Touch device optimizations */
    min-height: 44px;
    min-width: 44px;

    /* Disable hover effects on touch devices */
    &:hover {
      transform: none;
    }

    /* Add active state for better touch feedback */
    &:active {
      transform: scale(0.98);
    }
  }

  @media ${({ theme }) => theme.media.maxSm} {
    /* Mobile adjustments */
    ${({ size = 'md', theme }) => {
      switch (size) {
        case 'sm':
          return `
            padding: ${theme.spacing.xs} ${theme.spacing.sm};
            font-size: ${theme.fontSizes.xs};
          `;
        case 'lg':
          return `
            padding: ${theme.spacing.sm} ${theme.spacing.lg};
            font-size: ${theme.fontSizes.base};
          `;
        default:
          return `
            padding: ${theme.spacing.xs} ${theme.spacing.md};
            font-size: ${theme.fontSizes.sm};
          `;
      }
    }}
  }

  @media ${({ theme }) => theme.media.reducedMotion} {
    transition: none;

    &:active {
      transform: none;
    }
  }

  /* High contrast mode support */
  @media ${({ theme }) => theme.media.highContrast} {
    border-width: 2px;
  }
`;

export const StyledIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Touch device optimizations */
  @media ${({ theme }) => theme.media.touch} {
    width: 44px;
    height: 44px;
    
    &:hover {
      transform: none;
    }
    
    &:active {
      transform: scale(0.95);
      background-color: ${({ theme }) => theme.colors.backgroundSecondary};
    }
  }

  @media ${({ theme }) => theme.media.reducedMotion} {
    transition: none;
    
    &:active {
      transform: none;
    }
  }
`;
